import { integrationsEnabled, integrationsRequest } from '../lib/integrations';
import { currentUser } from './database';

export const pushSupported = () => integrationsEnabled && window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
let binding = 0;
export async function bindPushOwner(ownerId) {
  const generation = ++binding;
  if (!('serviceWorker' in navigator)) return;
  const registration = ownerId ? await navigator.serviceWorker.register('/reminder-sw.js') : await navigator.serviceWorker.getRegistration('/');
  if (!registration || generation !== binding) return;
  const active = registration.active || (await navigator.serviceWorker.ready).active;
  if (!active || generation !== binding) return;
  await new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => { channel.port1.close(); reject(new Error('Notification setup timed out. Reload the page.')); }, 10000);
    channel.port1.onmessage = () => { clearTimeout(timer); channel.port1.close(); resolve(); };
    active.postMessage({ type: 'SPCC_PUSH_OWNER', ownerId }, [channel.port2]);
  });
}
const keyBytes = value => Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4)), char => char.charCodeAt(0));
export async function browserPushStatus() {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  const registration = await navigator.serviceWorker.getRegistration('/');
  return await registration?.pushManager.getSubscription() ? 'enabled' : 'disabled';
}
export async function enableBrowserPush() {
  if (!pushSupported()) throw new Error('Browser push requires Firebase sign-in, HTTPS, and a supported browser. Calendar alerts are available on any device.');
  // Request permission directly from the user's click, before network requests.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications are blocked. Allow them in your browser site settings, then try again.');
  const ownerId = (await currentUser()).id;
  const { publicKey } = await integrationsRequest('/notifications/config');
  await bindPushOwner(ownerId);
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription() || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  if ((await currentUser()).id !== ownerId) throw new Error('Your account changed. Enable notifications again.');
  await integrationsRequest('/notifications/subscribe', { body: { subscription: subscription.toJSON(), time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone } });
  window.dispatchEvent(new Event('spcc:push-changed'));
}
export async function disableBrowserPush() {
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    await integrationsRequest('/notifications/unsubscribe', { body: { endpoint: subscription.endpoint } });
    await subscription.unsubscribe();
  }
  window.dispatchEvent(new Event('spcc:push-changed'));
}
export const testBrowserPush = () => integrationsRequest('/notifications/test', { body: {} });
